import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import { createCoupon, getPoints, getPromotions } from 'modules/loyaltyPoints/actions';
import UserLoyaltyPoints from 'routes/Home/components/UserLoyaltyPoints';
import useLiterals from 'utils/hooks/useLiterals';
import InlineError from 'components/UI/InlineError';
import Button from 'components/UI/Button';
import Modal from 'components/Modal';
import Translate from 'components/Translate';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import { ROUTE_PATH } from 'routes';
import Box from 'components/UI/Box/Box';
import { formatNumber } from 'utils/formatCurrency';
import Promotion from './components/Promotion';
import './styles.scss';

const LoyaltyPointsPromotions = () => {
  const literals = useLiterals('loyaltyPoints');
  const {
    promotions: { data: promotions, error: promotionsError },
    points: { data: points },
  } = useSelector((state) => state.loyaltyPoints);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [promotionSelected, setPromotionSelected] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(getPromotions());
    dispatch(getPoints());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const list = useMemo(() => {
    return {
      current: (promotions || [])?.filter((promotion) => !promotion.couponId),
      used: (promotions || [])?.filter((promotion) => promotion.couponId),
    };
  }, [promotions]);

  const handleCreateCoupon = (data, confirmed = false) => {
    if (confirmed) {
      dispatch(createCoupon(promotionSelected.id)).then((coupon) => {
        if (coupon?.id) {
          navigate(ROUTE_PATH.setLoyaltyPointsCoupon(coupon.id), { state: { isNew: true } });
        } else {
          navigate(ROUTE_PATH.LOYALTY_POINTS_COUPONS);
        }
      }).catch((e) => {
        setPromotionSelected(null);
        setError(e.message === true ? literals.errorCreatingCoupon : e.message);
      });
    } else {
      setPromotionSelected(data);
    }
  };

  return (
    <Layout
      className='loyalty-points-promotions'
      bottomMenu
      onBack
    >
      <AppShell variant='wide'>
        <UserLoyaltyPoints home={false} />
        <h2 className='mb-4'>{literals.currentPromotions}</h2>
        {promotionsError ? (
          <InlineError error={literals.errorLoadPromotions} className='mt-12' />
        ) : (
          <div className='lg:grid lg:grid-cols-2 lg:gap-x-3'>
            {!list.current.length ? (
              <Box padding='sm'>
                <p className='text-center mt-4 mb-4'>{literals.noPromotions}</p>
              </Box>
            ) : null}
            {list.current.map((promotion) => (
              <Promotion
                key={promotion.id}
                literals={literals}
                points={points}
                promotion={promotion}
                onCreateCoupon={handleCreateCoupon}
              />
            ))}
            {list.used.length ? (
              <>
                <h2 className='mb-4 mt-6'>{literals.usedPromotions}</h2>
                {list.used.map((promotion) => (
                  <Promotion
                    key={promotion.id}
                    literals={literals}
                    points={points}
                    promotion={promotion}
                    onCreateCoupon={handleCreateCoupon}
                  />
                ))}
              </>
            ) : null}
          </div>
        )}
        {/* Fuera del grid: como grid-item el botón se estiraba a la celda (óvalo
            gigante en escritorio, WEB_MLS-529). En flujo normal respeta w-full md:w-auto. */}
        {!promotionsError && (
          <Button className='mt-6' type='secondary' to={ROUTE_PATH.LOYALTY_POINTS_COUPONS}>{literals.couponHistory}</Button>
        )}
        {promotionSelected && (
          <Modal position='end' onClose={() => setPromotionSelected(null)} animation='slide-up-enter'>
            <Translate
              className='mt-4 text-lg text-center'
              literal={literals.confirmCreateCoupon}
              vars={{
                points: formatNumber(promotionSelected.points, 0),
                description: promotionSelected.description,
                newPoints: formatNumber((points?.available || 0) - promotionSelected.points, 0),
              }}
            />
            <Button
              className='mt-2'
              text={literals.common.confirm}
              onClick={() => handleCreateCoupon(promotionSelected, true)}
            />
            <Button
              className='mt-2'
              type='outline-primary'
              text={literals.common.cancel}
              onClick={() => setPromotionSelected(null)}
            />
          </Modal>
        )}
        {error && (
          <ErrorModal
            title={literals.common.error}
            description={error}
            onClose={() => setError(false)}
          />
        )}
      </AppShell>
    </Layout>
  );
};

export default LoyaltyPointsPromotions;
